<script lang="ts">
	import { clone, type JsonValue } from "@bufbuild/protobuf";
	import { Label, Select } from "flowbite-svelte-v1";
	import { PageSchema, type Page } from "#gen/pages/v2/page_pb.js";
	import P from "#lib/components/P.svelte";
	import IconPicker from "#lib/components/flow/editor/IconPicker.svelte";
	import ColorInputV2 from "#lib/components/flow/editor/appearance/ColorInputV2.svelte";
	import { BORDER_RADIUS_VALUES, BUTTON_COLOR_DEFAULTS, BUTTON_DEFAULT, BUTTON_SIZES, FONT_WEIGHTS, FULL_BORDER_RADIUS, HORIZONTAL_ALIGNMENTS } from "#lib/components/flow/editor/appearance/constants.js";
	import EditImage from "#lib/components/images/EditImage.svelte";
	import type { JsonObject } from "#lib/protobuf-compat.js";
	import { setStringValue, setStructValue, stringValue, structValue, unsetValue } from "#lib/struct.js";
	import type { Writable } from "svelte/store";

	interface Props {
		id: string;
		page: Writable<Page>;
		onChange: (value?: JsonValue) => void;
	}

	let { id, page, onChange }: Props = $props();

	const textAlignment = $derived(stringValue(structValue($page.config, id), "textAlignment") ?? BUTTON_DEFAULT.textAlignment);
	const fontWeight = $derived(stringValue(structValue($page.config, id), "fontWeight") ?? BUTTON_DEFAULT.fontWeight);
	const buttonSize = $derived(stringValue(structValue($page.config, id), "buttonSize") ?? BUTTON_DEFAULT.buttonSize);
	const borderRadius = $derived(stringValue(structValue($page.config, id), "borderRadius") ?? BUTTON_DEFAULT.borderRadius);
	const backgroundColor = $derived(stringValue(structValue($page.config, id), "backgroundColor"));
	const borderColor = $derived(stringValue(structValue($page.config, id), "borderColor"));
	const textColor = $derived(stringValue(structValue($page.config, id), "textColor"));
	const componentConfig = $derived(structValue($page.config, id));
	const imageLeftStruct = $derived(componentConfig ? structValue(componentConfig, "imageLeft") : undefined);
	const imageLeftUrl = $derived(imageLeftStruct ? stringValue(imageLeftStruct, "url") : undefined);
	const imageRightStruct = $derived(componentConfig ? structValue(componentConfig, "imageRight") : undefined);
	const imageRightUrl = $derived(imageRightStruct ? stringValue(imageRightStruct, "url") : undefined);
	const iconLeftStruct = $derived(componentConfig ? structValue(componentConfig, "iconLeft") : undefined);
	const iconLeftIconSet = $derived(iconLeftStruct ? (stringValue(iconLeftStruct, "iconSet") ?? stringValue(iconLeftStruct, "iconset")) : undefined);
	const iconLeftName = $derived(iconLeftStruct ? stringValue(iconLeftStruct, "name") : undefined);
	const iconRightStruct = $derived(componentConfig ? structValue(componentConfig, "iconRight") : undefined);
	const iconRightIconSet = $derived(iconRightStruct ? (stringValue(iconRightStruct, "iconSet") ?? stringValue(iconRightStruct, "iconset")) : undefined);
	const iconRightName = $derived(iconRightStruct ? stringValue(iconRightStruct, "name") : undefined);
	const iconColor = $derived(stringValue(structValue($page.config, id), "iconColor"));

	const resolvedBorderRadiusValues = $derived(BORDER_RADIUS_VALUES.concat({ name: "Full", value: FULL_BORDER_RADIUS }));

	function getConfig(): JsonObject {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		return config;
	}

	function getThemeColorForKey(key: string) {
		return stringValue($page.themeOverrides, key) ?? BUTTON_COLOR_DEFAULTS[key];
	}

	function handleChangeAppearance(e: Event, key: string) {
		const config = getConfig();
		const value = (e.target as HTMLSelectElement).value;
		if (value) {
			setStringValue(config, key, value);
		} else {
			unsetValue(config, key);
		}
		$page = $page;
		onChange?.();
	}

	function handleIconLeftChange(iconSet: string | undefined, name: string | undefined) {
		const config = getConfig();
		if (iconSet != null && name != null) {
			setStructValue(config, "iconLeft", { iconSet, name });
			unsetValue(config, "imageLeft");
		} else {
			unsetValue(config, "iconLeft");
		}
		$page = $page;
		onChange?.();
	}

	function handleIconRightChange(iconSet: string | undefined, name: string | undefined) {
		const config = getConfig();
		if (iconSet != null && name != null) {
			setStructValue(config, "iconRight", { iconSet, name });
			unsetValue(config, "imageRight");
		} else {
			unsetValue(config, "iconRight");
		}
		$page = $page;
		onChange?.();
	}

	function handleImageChange(key: "imageLeft" | "imageRight", value: string) {
		const config = getConfig();
		if (value) {
			setStructValue(config, key, { url: value });
		} else {
			unsetValue(config, key);
		}
		$page = $page;
		onChange?.();
	}

	function onColorInput(key: string, value: string) {
		const config = getConfig();
		if (value) {
			setStringValue(config, key, value);
			$page = clone(PageSchema, $page);
		}
	}

	function resetToPageStyle(key: string) {
		const config = structValue($page.config, id);
		if (config) {
			unsetValue(config, key);
			$page = $page;
			onChange?.();
		}
	}
</script>

<div class="flex flex-col gap-5">
	<P class="text-sm font-bold text-black">Appearance</P>
	<div class="flex flex-col gap-2">
		<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Button</P>
		<div class="grid grid-cols-2 gap-x-4 gap-y-3 border-l-[5px] border-l-gray-300 pl-6">
			<div class="flex flex-col gap-1">
				<Label for="{id}-button-size" class="font-medium text-black">Button size</Label>
				<Select classes={{ select: "truncate" }} items={BUTTON_SIZES} value={buttonSize} aria-labelledby="{id}-button-size" onchange={e => handleChangeAppearance(e, "buttonSize")} />
			</div>
			<div class="flex flex-col gap-1">
				<Label for="{id}-border-radius" class="font-medium text-black">Border radius</Label>
				<Select classes={{ select: "truncate" }} items={resolvedBorderRadiusValues} value={borderRadius} aria-labelledby="{id}-border-radius" onchange={e => handleChangeAppearance(e, "borderRadius")} />
			</div>
			<div class="flex flex-col gap-1">
				<Label for="{id}-background-color" class="font-medium text-black">Background color</Label>
				<ColorInputV2 id="{id}-background-color" allowTransparent resetColor="#000000" display="inline" value={backgroundColor || getThemeColorForKey("primaryButtonBackgroundColor")} showBrandKit={false} overridden={Boolean(backgroundColor)} onReset={() => resetToPageStyle("backgroundColor")} oninput={value => onColorInput("backgroundColor", value)} onchange={value => onChange?.(value)} />
			</div>
			<div class="flex flex-col gap-1">
				<Label for="{id}-border-color" class="font-medium text-black">Border color</Label>
				<ColorInputV2 id="{id}-border-color" resetColor="#000000" display="inline" value={borderColor || BUTTON_COLOR_DEFAULTS.borderColor} showBrandKit={false} oninput={value => onColorInput("borderColor", value)} onchange={value => onChange?.(value)} />
			</div>
		</div>
	</div>
	<div class="flex flex-col gap-2">
		<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Text</P>
		<div class="grid grid-cols-2 gap-x-4 gap-y-3 border-l-[5px] border-l-gray-300 pl-6">
			<div class="flex flex-col gap-1">
				<Label for="{id}-alignment-label" class="font-medium text-black">Text alignment</Label>
				<Select classes={{ select: "truncate" }} items={HORIZONTAL_ALIGNMENTS} value={textAlignment} aria-labelledby="{id}-alignment-label" onchange={e => handleChangeAppearance(e, "textAlignment")} />
			</div>
			<div class="flex flex-col gap-1">
				<Label for="{id}-font-weight-label" class="font-medium text-black">Font weight</Label>
				<Select classes={{ select: "truncate" }} items={FONT_WEIGHTS} value={fontWeight} aria-labelledby="{id}-font-weight-label" onchange={e => handleChangeAppearance(e, "fontWeight")} />
			</div>
			<div class="col-span-2 flex flex-col gap-1">
				<Label for="{id}-color-label" class="font-medium text-black">Text color</Label>
				<ColorInputV2 id="{id}-color-label" resetColor="#ffffff" display="inline" value={textColor || getThemeColorForKey("primaryButtonTextColor")} showBrandKit={false} overridden={Boolean(textColor)} onReset={() => resetToPageStyle("textColor")} oninput={value => onColorInput("textColor", value)} onchange={value => onChange?.(value)} />
			</div>
		</div>
	</div>
	<div class="flex flex-col gap-2">
		<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Icons</P>
		<div class="flex items-start gap-4 border-l-[5px] border-l-gray-300 pl-6">
			<div class="flex flex-col gap-1">
				<Label for="{id}-icon-left" class="font-medium text-black">Left</Label>
				<div class="h-10 w-10">
					{#if imageLeftUrl}
						<EditImage class="h-10.5 w-10.5 rounded-lg border-gray-300" dropzoneClass="h-full w-full border-none" imgClass="object-contain" value={imageLeftUrl} aspectRatio={1} on:change={e => handleImageChange("imageLeft", e.detail.value)} />
					{:else}
						<IconPicker id="{id}-icon-left" iconSet={iconLeftIconSet} name={iconLeftName} onChange={handleIconLeftChange} allowImageSelection onImageChange={value => handleImageChange("imageLeft", value)} />
					{/if}
				</div>
			</div>
			<div class="flex flex-col gap-1">
				<Label for="{id}-icon-right" class="font-medium text-black">Right</Label>
				<div class="h-10 w-10">
					{#if imageRightUrl}
						<EditImage class="h-10.5 w-10.5 rounded-lg border-gray-300" dropzoneClass="h-full w-full border-none" imgClass="object-contain" value={imageRightUrl} aspectRatio={1} on:change={e => handleImageChange("imageRight", e.detail.value)} />
					{:else}
						<IconPicker id="{id}-icon-right" iconSet={iconRightIconSet} name={iconRightName} onChange={handleIconRightChange} allowImageSelection onImageChange={value => handleImageChange("imageRight", value)} />
					{/if}
				</div>
			</div>
			<div class="flex grow flex-col gap-1">
				<Label for="{id}-icon-color" class="font-medium text-black">Color</Label>
				<ColorInputV2 id="{id}-icon-color" resetColor="#ffffff" display="inline" value={iconColor || BUTTON_COLOR_DEFAULTS.iconColor} showBrandKit={false} oninput={value => onColorInput("iconColor", value)} onchange={value => onChange?.(value)} />
			</div>
		</div>
	</div>
</div>
